import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FaTwitter, FaGlobe, FaCoins, FaWarehouse, FaChartLine, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const EL_API_ENDPOINT = 'https://berachain-v2-validators.blacknodes.net/api';

const ValidatorDetailsPage = () => {
  const { address } = useParams();
  const [validator, setValidator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchValidatorDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${EL_API_ENDPOINT}/validators/${address}`);
        console.log('Validator data:', response.data); // Add this line for debugging
        setValidator(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching validator details:', err);
        setError('Failed to load validator details. Please try again later.');
        setLoading(false);
      }
    };

    fetchValidatorDetails();
  }, [address]);

  if (loading) return <div className="text-center mt-8 text-white">Loading...</div>;
  if (error) return <div className="text-center mt-8 text-red-500">{error}</div>;
  if (!validator) return <div className="text-center mt-8 text-white">Validator not found</div>;

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            {validator.logoUri && (
              <img 
                src={validator.logoUri} 
                alt={`${validator.name} logo`} 
                className="w-16 h-16 mr-4 rounded-full object-contain bg-white"
                onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-logo.png'; }}
              />
            )}
            <div>
              <h1 className="text-3xl font-bold">{validator.name}</h1>
              <p className="text-gray-400">{validator.address}</p>
            </div>
          </div>
          <div className={`flex items-center ${validator.active ? 'text-green-500' : 'text-red-500'}`}>
            {validator.active ? (
              <>
                <FaCheckCircle className="mr-2" />
                <span>Active</span>
              </>
            ) : (
              <>
                <FaTimesCircle className="mr-2" />
                <span>Inactive</span>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="bg-gray-800 p-4 rounded-lg">
            <h2 className="text-xl mb-2">BGT Delegated</h2>
            <p className="text-2xl font-bold">{validator.amountStaked.toLocaleString()} BGT</p>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg">
            <h2 className="text-xl mb-2">BGT Queued</h2>
            <p className="text-2xl font-bold">{validator.amountQueued.toLocaleString()} BGT</p>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg">
            <h2 className="text-xl mb-2">Commission</h2>
            <p className="text-2xl font-bold">{(validator.commission * 100).toFixed(2)}%</p>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg">
            <h2 className="text-xl mb-2">APY</h2>
            <p className="text-2xl font-bold">{validator.apy.toFixed(2)}%</p>
          </div>
        </div>

        <div className="bg-gray-800 p-4 rounded-lg mb-8">
          <h2 className="text-xl mb-4 flex items-center"><FaWarehouse className="mr-2" /> Active Vaults</h2>
          <div className="grid grid-cols-3 gap-4">
            {validator.activeVaults.map((vault, index) => (
              <div key={index} className="bg-gray-700 p-3 rounded-lg flex items-center">
                {vault.logoUri && (
                  <img 
                    src={vault.logoUri} 
                    alt={`${vault.name} logo`} 
                    className="w-8 h-8 mr-2 rounded-full object-contain bg-white"
                    onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-logo.png'; }}
                  />
                )}
                <span>{vault.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-800 p-4 rounded-lg mb-8">
          <h2 className="text-xl mb-4 flex items-center"><FaChartLine className="mr-2" /> Performance Metrics</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-lg mb-2">All Time BGT Directed</h3>
              <p className="text-xl font-bold">{validator.allTimeData.allTimeBgtDirected.toLocaleString()} BGT</p>
            </div>
            <div>
              <h3 className="text-lg mb-2">All Time Unique Token Count</h3>
              <p className="text-xl font-bold">{validator.allTimeData.allTimeUniqueTokenCount}</p>
            </div>
            <div>
              <h3 className="text-lg mb-2">All Time Honey Value Token Rewards</h3>
              <p className="text-xl font-bold">${validator.allTimeData.allTimeHoneyValueTokenRewards.toLocaleString()}</p>
            </div>
            <div>
              <h3 className="text-lg mb-2">All Time Honey Value BGT Directed</h3>
              <p className="text-xl font-bold">${validator.allTimeData.allTimeHoneyValueBgtDirected.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 p-4 rounded-lg mb-8">
          <h2 className="text-xl mb-4 flex items-center"><FaCoins className="mr-2" /> Active Incentives</h2>
          <div className="space-y-4">
            {validator.activeIncentives.map((incentive, index) => (
              <div key={index} className="bg-gray-700 p-3 rounded-lg">
                <h3 className="text-lg font-bold mb-2">{incentive.token.name} ({incentive.token.symbol})</h3>
                <p>Amount Left: {incentive.amountLeft.toLocaleString()} {incentive.token.symbol}</p>
                <p>Incentive Rate: {incentive.incentiveRate}%</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-800 p-4 rounded-lg">
          <h2 className="text-xl mb-4">Validator Information</h2>
          <div className="flex items-center mb-2">
            <FaGlobe className="mr-2" />
            <a href={validator.website} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">{validator.website}</a>
          </div>
          <div className="flex items-center mb-4">
            <FaTwitter className="mr-2" />
            <a href={`https://twitter.com/${validator.twitter}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">@{validator.twitter}</a>
          </div>
          <p className="mt-4"><strong>Description:</strong> {validator.description}</p>
        </div>
      </div>
    </div>
  );
};

export default ValidatorDetailsPage;